import {
  DateFormat,
  EventCard,
  EventCardProps,
  getAgeFromDob,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { capitalizeFirstLetter } from '~/lib/common';
import {
  formatDateWithTimezone,
  getAttendeePronouns,
  getRecurrenceDisplay,
  Recurrence,
} from '~/lib/util/calendar.util';
import { getTimezoneAbbreviation } from '~/lib/util/timezone.util';
import { Path } from '~/routes/constants';

interface AppointmentSummaryProps {
  patient: Patient;
  provider: GetProviderByIdResponse;
  startTime: string;
  endTime: string;
  timeZone: string;
}

const formatTime = (date: string, timeZone: string): string =>
  DateTime.fromISO(date, { zone: timeZone })
    .toFormat(DateFormat.timeWithoutSpace)
    .toLowerCase();

const AppointmentSummary = ({
  provider,
  startTime,
  endTime,
  patient,
  timeZone,
}: AppointmentSummaryProps) => {
  const getCredentials = (provider: GetProviderByIdResponse) => {
    if (
      !provider?.credentialsDetails ||
      provider?.credentialsDetails.length === 0
    ) {
      return undefined;
    }
    return provider.credentialsDetails.map((each) => each.name);
  };

  const handleViewPatientProfileClick = (patientId: string): string => {
    return `${window.location.origin}${Path.MAUD_PATIENTS}/${patientId}`;
  };

  const eventCardProps: EventCardProps = {
    eventType: 'appointment',
    scheduleItem: {
      attendees: [
        {
          avatarUrl: provider?.avatar?.url,
          credentials: getCredentials(provider),
          externalId: provider.userId,
          isAttended: false,
          isHost: true,
          name: `${capitalizeFirstLetter(provider?.chosenName || provider?.firstName)} ${capitalizeFirstLetter(provider?.lastName)}`,
          pronouns: getAttendeePronouns(provider?.pronouns),
          userType: provider?.providerTypeDetails?.name,
        },
        {
          avatarUrl: patient.avatarSrc,
          externalId: patient.userId,
          isAttended: false,
          isHost: false,
          name: `${capitalizeFirstLetter(patient?.chosenName || patient?.firstName)} ${capitalizeFirstLetter(patient?.lastName)}`,
          pronouns: getAttendeePronouns(patient?.pronounsOnRecord),
          userType: 'Patient',
        },
      ],
      eventDate: formatDateWithTimezone(
        startTime,
        `${DateFormat.weekdayShortMonthDateNoSpecialChar}`,
        timeZone,
      ),
      eventExternalId: '',
      eventName: `Admission consultation, 30 minutes`,
      eventTime: `${formatTime(startTime, timeZone)} - ${formatTime(endTime, timeZone)} ${getTimezoneAbbreviation(timeZone, startTime)}`,
      frequencyText: getRecurrenceDisplay(
        startTime,
        Recurrence.DOES_NOT_REPEAT,
        DateTime.fromISO(startTime).plus({ months: 6, days: -1 }).toISODate(),
        true,
      ),
      patient: {
        age: getAgeFromDob(patient?.dateOfBirth),
        name: `${capitalizeFirstLetter(patient?.chosenName || patient?.firstName)} ${capitalizeFirstLetter(patient?.lastName)}`,
      },
      shouldShowViewPatientProfileButton: true,
      shouldShowZoomButton: false,
    },
    patientProfileLink: handleViewPatientProfileClick(patient.userId),
  };

  return <EventCard {...eventCardProps} />;
};

export default AppointmentSummary;
